import { useCallback, useState } from 'react';
import {
  DEFAULT_PAGE_SIZE,
  needsPaging,
  pageWindow,
  type PageWindow,
  type PagerControls,
} from '../pagination';

/** Los renglones de la página, y con qué moverse. Lo segundo es lo que recibe `Pagination`. */
export interface Pager<T> extends PageWindow<T>, PagerControls {}

/**
 * El paginado de una lista en memoria.
 *
 * `resetKey` es lo que la lista está filtrando —la sub área elegida, lo
 * tecleado en el buscador—. Cuando cambia se vuelve a la primera página, que es
 * donde alguien espera ver los resultados de un filtro nuevo. Sin él la página
 * sólo se ajusta a lo que haya, y eso también importa: una lista que se recarga
 * sola después de guardar no debe devolver a nadie al principio.
 *
 * Se ajusta al pintar y no con un efecto: un efecto pintaría primero la página
 * vieja sobre el filtro nuevo —un fotograma de renglones que no tocan— y
 * después la corregiría. Y el filtro anterior se olvida: volver a «Todas»
 * después de mirar una sub área empieza otra vez por el principio, no en la
 * página donde se había quedado.
 */
export const usePagination = <T>(all: readonly T[], resetKey = ''): Pager<T> => {
  const [state, setState] = useState({ page: 1, key: resetKey });
  const [pageSize, setSize] = useState<number>(DEFAULT_PAGE_SIZE);

  if (state.key !== resetKey) setState({ page: 1, key: resetKey });
  const requested = state.key === resetKey ? state.page : 1;
  const view = pageWindow(all, requested, pageSize);

  const goTo = useCallback(
    (page: number): void => {
      setState({ page, key: resetKey });
    },
    [resetKey],
  );

  /* Cambiar el tamaño vuelve al principio: la «página 3» de diez en diez no es
     la misma gente que la de cincuenta en cincuenta. */
  const setPageSize = useCallback(
    (size: number): void => {
      setSize(size);
      setState({ page: 1, key: resetKey });
    },
    [resetKey],
  );

  return { ...view, pageSize, needed: needsPaging(all.length), goTo, setPageSize };
};
